// Deep imports, not the features' barrels: `features/sources` reaches back
// into `logbook/draft.ts`, and `features/flights` into `logbook/derive-stats`,
// so routing either of these through a barrel would close a module-level
// cycle. Same note `features/flights/link.ts` carries, for the same reason.
import { linkTrackToFlight } from '@/features/flights/link';
import { DEFAULT_SETTINGS, SETTINGS_KEYS } from '@/features/settings';
import { fetchAndImport, type FetchTarget } from '@/features/sources/fetch-import';
import type { Aircraft, Flight, LogbookEntry, Track } from '@/lib/db';
import {
  aircraft as defaultAircraftRepo,
  logbookEntries as defaultLogbookRepo,
  settings as defaultSettingsRepo,
  tracks as defaultTrackRepo,
} from '@/lib/db';
import type {
  AircraftRepository,
  FlightRepository,
  LogbookRepository,
  SettingsRepository,
  TrackRepository,
} from '@/lib/db';
import { SourceError } from '@/lib/sources';
import type { TrackCandidate, TrackSource } from '@/lib/sources';

import type { LogbookColumn } from './columns';
import { columnById, LOGBOOK_COLUMNS } from './columns';
import { applyTrackValues, trackDerivedValues } from './derived-values';
import { applyDerivedValue } from './entry';
import type { MatchScore } from './match';
import { scoreCandidate } from './match';

/**
 * Matching a manually-typed logbook entry to a flight an API knows about —
 * `PLAN.md` §6 Phase 5, the half that was left under Later/optional until the
 * adapters existed.
 *
 * *A pilot can write a logbook entry by hand before any track exists for it...
 * the app can search the API for a flight matching that manual entry and
 * surface it as a suggested match rather than auto-linking it — same "track is
 * evidence, not truth" rule as file import. The pilot reviews the suggested
 * match, confirms or rejects it, and only on approval does the entry pick up
 * the derived track fields.*
 *
 * Three steps, in that order, each one a thing the pilot asked for:
 *
 * 1. **`findApiMatches`** searches the source around the entry's own date and
 *    ranks what comes back (`match.ts`). Nothing is downloaded and nothing is
 *    changed. On a metered source this is the first of the two credit charges.
 * 2. **`acceptApiMatch`** fetches the one flight the pilot picked, imports it
 *    through the ordinary path (so it is deduplicated against tracks already
 *    held), links it to the entry's flight, and returns a per-field diff. It
 *    does *not* alter a single logbook cell.
 * 3. **`applyProposals`** writes the fields the pilot ticked, and only those.
 *
 * Splitting 2 from 3 is what makes `DATA-SOURCES.md` §4's rule enforceable
 * rather than aspirational: *never auto-merge silently — show what changes, and
 * keep the pilot's typed values over derived ones on conflict, with a visible
 * "track says X, you logged Y" diff.* A conflicting field arrives switched off.
 * A blank one arrives switched on, because filling a blank overwrites nothing.
 */

/** How far either side of the entry's own date to look. A flight is logged on the day it landed. */
export const MATCH_WINDOW_DAYS = 1;

export interface SuggestedMatch {
  candidate: TrackCandidate;
  score: MatchScore;
}

export interface ApiMatchDeps {
  aircraftRepo?: AircraftRepository;
  flightRepo?: FlightRepository;
  logbookRepo?: LogbookRepository;
  settingsRepo?: SettingsRepository;
  trackRepo?: TrackRepository;
  fetchImport?: typeof fetchAndImport;
  linkTrack?: typeof linkTrackToFlight;
  now?: () => string;
}

async function aircraftFor(
  entry: LogbookEntry,
  repo: AircraftRepository,
): Promise<Aircraft | undefined> {
  if (entry.aircraftId !== undefined) {
    return repo.get(entry.aircraftId);
  }
  if (entry.registration !== undefined) {
    return repo.findByRegistration(entry.registration);
  }
  return undefined;
}

/**
 * Flights this source knows about that could be this entry, best first.
 *
 * @throws SourceError — from the adapter, unchanged. A rejected key and an
 *   empty result are different answers and the caller needs to tell them apart.
 */
export async function findApiMatches(
  entry: LogbookEntry,
  flight: Flight | undefined,
  source: TrackSource,
  deps: ApiMatchDeps = {},
): Promise<SuggestedMatch[]> {
  const aircraftRepo = deps.aircraftRepo ?? defaultAircraftRepo;
  const settingsRepo = deps.settingsRepo ?? defaultSettingsRepo;

  if (source.search === undefined) {
    throw new SourceError('unsupported', `${source.label} cannot be searched for a flight.`);
  }

  const timeZone = await settingsRepo.get(SETTINGS_KEYS.timeZone, DEFAULT_SETTINGS.timeZone);
  const aircraft = await aircraftFor(entry, aircraftRepo);
  const registration = entry.registration ?? aircraft?.registration;

  /*
   * The window is built from the entry's own local date, then widened a day
   * each way — a flight that left at 2200 NZDT is logged on one calendar day
   * and sits on the previous one in UTC, and either could be what the pilot
   * wrote down.
   */
  const midnight = Date.parse(`${entry.date}T00:00:00Z`);
  if (!Number.isFinite(midnight)) {
    throw new SourceError('unsupported', `"${entry.date}" is not a date this app can search from.`);
  }
  const from = new Date(midnight - MATCH_WINDOW_DAYS * 86_400_000);
  const to = new Date(midnight + (MATCH_WINDOW_DAYS + 1) * 86_400_000);

  const candidates = await source.search({
    ...(registration === undefined ? {} : { registration }),
    ...(aircraft?.icaoHex === undefined ? {} : { icaoHex: aircraft.icaoHex }),
    from,
    to,
  });

  return candidates
    .map((candidate) => ({
      candidate,
      score: scoreCandidate(entry, flight, candidate, { timeZone }),
    }))
    .sort((a, b) => b.score.score - a.score.score);
}

/*
 * A column label is only unique within its group. A paper logbook page has a
 * "Day" under Time and another under Landings, and the grid tells them apart
 * by the group header above the column — which a flat list of proposals has
 * nowhere to put. So the ambiguous ones carry their group and the rest stay
 * short.
 */
const AMBIGUOUS_LABELS = new Set(
  LOGBOOK_COLUMNS.map((column) => column.label).filter(
    (label, index, all) => all.indexOf(label) !== index,
  ),
);

function proposalLabel(column: LogbookColumn): string {
  return AMBIGUOUS_LABELS.has(column.label)
    ? `${column.groupLabel} · ${column.label}`
    : column.label;
}

/** One field the track would change, and what it currently says. */
export interface FieldProposal {
  columnId: string;
  label: string;
  /** What the entry says now. Empty for a blank cell. */
  typed: string;
  /** What the track says. */
  proposed: string;
  /** True when the pilot already typed something different here. */
  conflict: boolean;
}

export interface MatchAccepted {
  outcome: 'accepted';
  track: Track;
  flight: Flight;
  entry: LogbookEntry;
  proposals: FieldProposal[];
}

export interface MatchFailed {
  outcome: 'failed';
  message: string;
  detail?: string[];
  errorKind?: SourceError['kind'];
}

export type MatchResult = MatchAccepted | MatchFailed;

/**
 * Fetch the picked flight, link it to this entry, and work out what it would
 * change.
 *
 * The link is made and the logbook is not touched. That is deliberate: a
 * linked track is evidence attached to the flight, which is useful on its own
 * — the map, the derived stats, the flight page — and is exactly what a pilot
 * who rejects the field-by-field merge should still be left holding. They paid
 * for the track; they keep the track.
 */
export async function acceptApiMatch(
  entry: LogbookEntry,
  source: TrackSource,
  /** The candidate, so the fetch keeps the identity the search established. */
  target: FetchTarget,
  deps: ApiMatchDeps = {},
): Promise<MatchResult> {
  const aircraftRepo = deps.aircraftRepo ?? defaultAircraftRepo;
  const trackRepo = deps.trackRepo ?? defaultTrackRepo;

  /*
   * `draft: false` is the one thing this does differently from the search
   * screen's own fetch. There is already an entry for this flight — the one
   * the pilot typed — and drafting a second one beside it is the duplicate
   * row that the whole draft-and-approve dedup exists to avoid.
   */
  const result = await (deps.fetchImport ?? fetchAndImport)(source, target, { draft: false });
  if (result.outcome === 'failed' || result.track === undefined) {
    return {
      outcome: 'failed',
      message: result.message ?? `${source.label} returned no track for that flight.`,
      ...(result.detail === undefined ? {} : { detail: result.detail }),
      ...(result.errorKind === undefined ? {} : { errorKind: result.errorKind }),
    };
  }
  const track = result.track;

  // The repositories go through too: `linkTrackToFlight` defaults to the
  // singleton database, and a caller that handed one in here meant that one.
  const linked = await (deps.linkTrack ?? linkTrackToFlight)(entry.flightId, track.id, {
    ...(deps.flightRepo === undefined ? {} : { flightRepo: deps.flightRepo }),
    ...(deps.logbookRepo === undefined ? {} : { logbookRepo: deps.logbookRepo }),
    ...(deps.trackRepo === undefined ? {} : { trackRepo: deps.trackRepo }),
    ...(deps.now === undefined ? {} : { now: deps.now }),
  });

  const points = await trackRepo.getPoints(track.id);
  if (points === undefined) {
    // The track is stored and linked; only the diff is unavailable, and saying
    // so beats pretending the match failed.
    return {
      outcome: 'accepted',
      track,
      flight: linked.flight,
      entry,
      proposals: [],
    };
  }

  const aircraft = await aircraftFor(entry, aircraftRepo);
  const values = trackDerivedValues(track, points, aircraft);
  const asTheTrackWouldHaveIt = applyTrackValues(entry, values);

  const proposals: FieldProposal[] = [];
  for (const columnId of values.derivedFields) {
    const column = columnById(columnId);
    if (column === undefined) {
      continue;
    }
    const typed = column.read(entry);
    const proposed = column.read(asTheTrackWouldHaveIt);
    if (typed === proposed) {
      continue;
    }
    proposals.push({
      columnId,
      label: proposalLabel(column),
      typed,
      proposed,
      conflict: typed !== '',
    });
  }
  // The grid's own column order, so the diff reads down the page the same way
  // the row reads across it.
  const order = new Map(LOGBOOK_COLUMNS.map((column, index) => [column.id, index]));
  proposals.sort((a, b) => (order.get(a.columnId) ?? 0) - (order.get(b.columnId) ?? 0));

  // Re-read: `linkTrackToFlight` can clear a stale `derivedFields`, and the
  // caller needs the entry as it now is on disk, not as it was handed in.
  const current = (await (deps.logbookRepo ?? defaultLogbookRepo).get(entry.id)) ?? entry;

  return { outcome: 'accepted', track, flight: linked.flight, entry: current, proposals };
}

/** Which proposals a pilot has ticked. Conflicts start unticked; blanks start ticked. */
export function defaultAcceptedIds(proposals: readonly FieldProposal[]): string[] {
  return proposals.filter((proposal) => !proposal.conflict).map((proposal) => proposal.columnId);
}

/**
 * Write the accepted proposals into the entry and save it.
 *
 * Each goes through `applyDerivedValue`, so the value is recorded as the
 * track's rather than the pilot's — underlined in the grid until they type
 * over it — and the previous value joins `history` rather than vanishing.
 *
 * @returns the saved entry, or the original when nothing was accepted.
 */
export async function applyProposals(
  entry: LogbookEntry,
  proposals: readonly FieldProposal[],
  acceptedIds: readonly string[],
  deps: ApiMatchDeps = {},
): Promise<LogbookEntry> {
  const logbookRepo = deps.logbookRepo ?? defaultLogbookRepo;
  const now = deps.now ?? (() => new Date().toISOString());

  const accepted = new Set(acceptedIds);
  let next = entry;
  for (const proposal of proposals) {
    if (!accepted.has(proposal.columnId)) {
      continue;
    }
    const applied = applyDerivedValue(next, proposal.columnId, proposal.proposed, now());
    if (applied.ok && applied.entry !== undefined) {
      next = applied.entry;
    }
  }

  if (next === entry) {
    return entry;
  }
  await logbookRepo.put(next);
  return next;
}
